import { BoxCharacters } from '@fragment/types/boxChars';
import type { AccountType } from '@fragment/ui/src/components/utils';

export const exampleBalances = [
  {
    account: 'assets',
    amount: '125,554.26 USD',
    type: 'asset',
  },
  {
    account: 'assets/cash',
    amount: '100,336.61 USD',
    type: 'asset',
  },
  {
    account: 'assets/cash/operating',
    amount: '80,474.18 USD',
    type: 'asset',
  },
  {
    account: 'assets/cash/operating/domestic',
    amount: '60,327.81 USD',
    type: 'asset',
  },
  {
    account: 'assets/cash/operating/international',
    amount: '20,146.37 USD',
    type: 'asset',
  },
  {
    account: 'assets/cash/reserves',
    amount: '19,862.43 USD',
    type: 'asset',
  },
  {
    account: 'assets/receivables',
    amount: '25,217.65 USD',
    type: 'asset',
  },
  {
    account: 'assets/receivables/pending',
    amount: '18,082.76 USD',
    type: 'asset',
  },
  {
    account: 'assets/receivables/settled',
    amount: '7,134.89 USD',
    type: 'asset',
  },
  {
    account: 'liabilities',
    amount: '98,295.15 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/customers',
    amount: '74,779.38 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/customers/available',
    amount: '64,641.67 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/customers/available/business',
    amount: '45,321.87 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/customers/available/consumer',
    amount: '19,319.80 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/customers/pending',
    amount: '10,137.71 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/payouts',
    amount: '23,515.77 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/payouts/queued',
    amount: '17,642.58 USD',
    type: 'liability',
  },
  {
    account: 'liabilities/payouts/in-transit',
    amount: '5,873.19 USD',
    type: 'liability',
  },
  {
    account: 'income',
    amount: '32,110.73 USD',
    type: 'income',
  },
  {
    account: 'income/processing-fees',
    amount: '18,764.23 USD',
    type: 'income',
  },
  {
    account: 'income/subscriptions',
    amount: '10,220.03 USD',
    type: 'income',
  },
  {
    account: 'income/subscriptions/monthly',
    amount: '7,582.19 USD',
    type: 'income',
  },
  {
    account: 'income/subscriptions/annual',
    amount: '2,637.84 USD',
    type: 'income',
  },
  {
    account: 'income/transfer-fees',
    amount: '3,126.47 USD',
    type: 'income',
  },
  {
    account: 'expense',
    amount: '4,851.62 USD',
    type: 'expense',
  },
  {
    account: 'expense/bank-fees',
    amount: '1,273.18 USD',
    type: 'expense',
  },
  {
    account: 'expense/processing',
    amount: '3,039.73 USD',
    type: 'expense',
  },
  {
    account: 'expense/processing/domestic',
    amount: '2,276.49 USD',
    type: 'expense',
  },
  {
    account: 'expense/processing/international',
    amount: '763.24 USD',
    type: 'expense',
  },
  {
    account: 'expense/chargebacks',
    amount: '538.71 USD',
    type: 'expense',
  },
] satisfies { account: string; amount: string; type: AccountType }[];

export const exampleEntries = [
  {
    id: 'payment-received',
    count: 984,
    accountTypes: ['asset', 'liability'],
  },
  {
    id: 'payment-settled',
    count: 856,
    accountTypes: ['asset'],
  },
  {
    id: 'processing-fee-collected',
    count: 742,
    accountTypes: ['liability', 'income'],
  },
  {
    id: 'funds-available',
    count: 412,
    accountTypes: ['liability'],
  },
  {
    id: 'payout-requested',
    count: 328,
    accountTypes: ['liability'],
  },
  {
    id: 'payout-processing',
    count: 297,
    accountTypes: ['liability'],
  },
  {
    id: 'payout-completed',
    count: 265,
    accountTypes: ['liability', 'asset'],
  },
  {
    id: 'business-funded',
    count: 236,
    accountTypes: ['asset', 'liability'],
  },
  {
    id: 'subscription-billed',
    count: 192,
    accountTypes: ['liability', 'income'],
  },
  {
    id: 'customer-funded',
    count: 128,
    accountTypes: ['asset', 'liability'],
  },
  {
    id: 'international-transfer',
    count: 87,
    accountTypes: ['asset'],
  },
  {
    id: 'transfer-fee-collected',
    count: 79,
    accountTypes: ['liability', 'income'],
  },
  {
    id: 'reserve-funded',
    count: 64,
    accountTypes: ['asset'],
  },
  {
    id: 'reserve-released',
    count: 52,
    accountTypes: ['asset'],
  },
  {
    id: 'annual-plan-billed',
    count: 46,
    accountTypes: ['liability', 'income'],
  },
  {
    id: 'bank-fee-charged',
    count: 34,
    accountTypes: ['expense', 'asset'],
  },
  {
    id: 'payout-returned',
    count: 18,
    accountTypes: ['asset', 'liability'],
  },
  {
    id: 'chargeback-received',
    count: 12,
    accountTypes: ['expense', 'asset'],
  },
] satisfies { id: string; count: number; accountTypes: AccountType[] }[];

type ExampleAccountRow = (typeof exampleBalances)[number] & {
  label: string;
  branch: string;
  isRoot: boolean;
};

const getAccountRows = (
  parentPath: string,
  ancestorBranch: string
): ExampleAccountRow[] => {
  const siblings = exampleBalances.filter(
    ({ account }) =>
      account.slice(0, account.lastIndexOf('/') + 1) === parentPath
  );
  return siblings.flatMap((balance, index) => {
    const isRoot = parentPath === '';
    const isLast = index === siblings.length - 1;
    const branch = isRoot
      ? ''
      : ancestorBranch +
        (isLast ? BoxCharacters.RIGHT_BOTTOM_ELBOW : BoxCharacters.RIGHT_TEE);
    const childBranch = isRoot
      ? ''
      : ancestorBranch + (isLast ? '  ' : `${BoxCharacters.VERTICAL_LINE} `);
    return [
      {
        ...balance,
        label: balance.account.slice(balance.account.lastIndexOf('/') + 1),
        branch,
        isRoot,
      },
      ...getAccountRows(`${balance.account}/`, childBranch),
    ];
  });
};

export const exampleAccountRows = getAccountRows('', '');
